import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './calendar.css'; // Estilos personalizados
import { parseTimeTo24h } from '../utils/calendarUtils';
import { XMarkIcon, CalendarDaysIcon, ClockIcon } from '@heroicons/react/24/outline';
Modal.setAppElement('#root');

const CalendarComponent = () => {
  const [events, setEvents] = useState([]); // Lista de eventos
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // Fecha seleccionada
  const [selectedTime, setSelectedTime] = useState(null); // Hora seleccionada
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: '',
    needs: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedTimes, setBookedTimes] = useState([]); // Horarios reservados para el día seleccionado
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // Horas disponibles de 8 AM a 8 PM en intervalos de 2 horas
  const slots = [
    { time: '08:00 AM', available: true },
    { time: '10:00 AM', available: true },
    { time: '12:00 PM', available: true },
    { time: '02:00 PM', available: true },
    { time: '04:00 PM', available: true },
    { time: '06:00 PM', available: true },
    { time: '08:00 PM', available: true },
  ];

  // Traer los horarios ya ocupados en el calendario real para la fecha seleccionada
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedDate) {
        setEvents([]);
        return;
      }
      setLoadingAvailability(true);
      try {
        const dateStr = selectedDate.toISOString().split('T')[0];
        const response = await fetch(
          `https://us-central1-tarjeta-ad945.cloudfunctions.net/getCalendarAvailability?date=${dateStr}`
        );
        const result = await response.json();
        const busyRanges = (result.busy || []).map(({ start, end }) => ({
          start: new Date(start),
          end: new Date(end),
        }));

        // Un slot cuenta como ocupado si su hora de inicio cae dentro de algún rango ocupado
        const blockedEvents = slots.reduce((acc, slot) => {
          const parsedTime = parseTimeTo24h(slot.time);
          if (!parsedTime) return acc;
          const [hours] = parsedTime.split(':').map(Number);
          const slotStart = new Date(selectedDate);
          slotStart.setHours(hours, 0, 0, 0);

          const isBusy = busyRanges.some(
            (range) => slotStart >= range.start && slotStart < range.end
          );
          if (isBusy) acc.push({ start: slotStart.toISOString() });
          return acc;
        }, []);

        setEvents(blockedEvents);
      } catch (error) {
        console.error('❌ Error al consultar disponibilidad:', error);
        setEvents([]);
      } finally {
        setLoadingAvailability(false);
      }
    };

    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Actualizar los slots disponibles según las reglas y eventos ya existentes
  useEffect(() => {
    if (selectedDate) {
      const booked = events.filter(event =>
        new Date(event.start).toDateString() === selectedDate.toDateString()
      ).map(event => new Date(event.start).getHours());

      setBookedTimes(booked);

      const currentHour = new Date().getHours();
      const today = new Date().toDateString() === selectedDate.toDateString();
      const updatedSlots = slots.map(slot => {
        const parsedTime = parseTimeTo24h(slot.time);
        if (!parsedTime) {
          return { ...slot, available: false };
        }
        const slotHour = parseInt(parsedTime.split(':')[0], 10);

        // Deshabilitar slots si ya están reservados o si están en el pasado
        if (booked.includes(slotHour) || (today && slotHour <= currentHour)) {
          return { ...slot, available: false };
        }
        return { ...slot, available: true };
      });

      setAvailableSlots(updatedSlots);
    }
  }, [selectedDate, events]);

  // Abrir el modal para seleccionar la fecha y hora
  const openModal = () => {
    setModalIsOpen(true);
  };

  // Cerrar el modal y resetear los campos
  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      businessType: '',
      needs: '',
    });
  };

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Si el cliente selecciona un slot de hora
  const handleSlotSelection = (time) => {
    setSelectedTime(time); // Guardar la hora seleccionada
  };

  // Crear un objeto de fecha y hora en formato ISO para Google Calendar
  const combineDateAndTime = (date, time) => {
    const parsed = parseTimeTo24h(time);
    if (!parsed) return null;
    const [hours, minutes] = parsed.split(':');
    const combined = new Date(date);
    combined.setHours(parseInt(hours, 10));
    combined.setMinutes(parseInt(minutes, 10));
    combined.setSeconds(0);
    combined.setMilliseconds(0);
    return combined;
  };

  // Enviar el formulario y agregar el evento
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!selectedTime) {
      alert("Por favor, selecciona una hora");
      return;
    }
  
    // Combinar fecha y hora seleccionadas correctamente
    const startDateTime = combineDateAndTime(selectedDate, selectedTime);
    if (!startDateTime || Number.isNaN(startDateTime.getTime())) {
      alert("Fecha u hora inválidas");
      return;
    }
    const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000); // Duración: 1 hora

    // Descripción completa para que quien llame tenga todo el contexto sin buscar en otro sistema
    const descriptionLines = [
      `Teléfono: ${formData.phone}`,
      `Email: ${formData.email}`,
      formData.businessType ? `Tipo de negocio: ${formData.businessType}` : null,
      formData.needs ? `Qué necesita: ${formData.needs}` : null,
      'Origen: Demo agendada desde bookme (calendario en el hero)',
    ].filter(Boolean);

    const newEvent = {
      title: `Demo Book Me: ${formData.name} (${formData.businessType || 'sin especificar'})`,
      description: descriptionLines.join('\n'),
      start: startDateTime.toISOString(),
      end: endDateTime.toISOString(),
      email: formData.email,
      name: formData.name,
      phone: formData.phone,
      businessType: formData.businessType,
    };

    setSubmitting(true);
    try {
      const response = await fetch(
        'https://us-central1-tarjeta-ad945.cloudfunctions.net/createCalendarEvent',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newEvent),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        alert(`Error al crear el evento: ${result.error}`);
      } else {
        console.log('✅ Evento creado en Google Calendar:', result);

        // Registrar también como lead en el CRM, para que no quede solo en el calendario aislado
        try {
          const sessionId = sessionStorage.getItem('sessionId') || null;
          await fetch('https://registerlandinglead-xjpm53xzha-uc.a.run.app', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              source: 'bookme_calendar',
              sessionId,
              status: 'demo_agendada',
              meta: {
                businessType: formData.businessType,
                needs: formData.needs,
                scheduledFor: startDateTime.toISOString(),
              },
            }),
          });
        } catch (leadError) {
          console.warn('⚠️ El evento se creó pero no se pudo registrar el lead en el CRM:', leadError);
        }

        alert('¡Listo! Tu demo quedó agendada y confirmada por correo.');
      }
    } catch (error) {
      console.error('❌ Error al crear el evento:', error);
      alert('Hubo un error al crear el evento.');
    }

    setSubmitting(false);
    closeModal();
  };
  
  

  return (
    <div className="text-center">
      <button
        onClick={openModal}
        className="px-6 py-3 bg-orange-500 text-white font-semibold rounded-full shadow-md hover:bg-orange-600 transition"
      >
        Seleccionar Fecha y Hora
      </button>
  
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Selecciona una fecha y hora"
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <div className="relative bg-white rounded-2xl p-8 shadow-xl max-w-2xl mx-auto text-left">
          {/* Botón Cerrar */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 text-gray-400 hover:text-black transition"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
  
          {/* Título + icono */}
          <div className="flex items-center gap-3 mb-6">
            <CalendarDaysIcon className="w-7 h-7 text-orange-500" />
            <h2 className="text-2xl font-bold text-gray-800">Selecciona una fecha</h2>
          </div>
  
          {/* Calendario */}
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            minDate={new Date()}
            dateFormat="MMMM d, yyyy"
            inline
          />
  
          {/* Horarios disponibles */}
          {selectedDate && (
            <>
              <div className="mt-6 mb-4 flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-orange-500" />
                <h3 className="text-lg font-semibold text-gray-700">Selecciona una hora</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {loadingAvailability ? (
                  <p className="text-sm text-gray-500 italic col-span-full">
                    Buscando disponibilidad...
                  </p>
                ) : availableSlots.length > 0 ? (
                  availableSlots.map((slot, index) => (
                    <button
                      key={index}
                      className={`py-2 px-4 rounded-lg border transition text-sm font-medium ${
                        slot.available
                          ? 'bg-orange-50 text-orange-600 hover:bg-orange-100 border-orange-200'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      onClick={() => handleSlotSelection(slot.time)}
                      disabled={!slot.available}
                    >
                      {slot.time}
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic col-span-full">
                    No hay horarios disponibles, selecciona otra fecha.
                  </p>
                )}
              </div>
            </>
          )}
  
          {/* Formulario visible solo si hay hora seleccionada */}
          {selectedTime && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Nombre"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Teléfono"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <select
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Tipo de Negocio</option>
                  <option value="Spa / Clínica Estética">Spa / Clínica Estética</option>
                  <option value="Restauración / Seguros">Restauración / Seguros</option>
                  <option value="Restaurantes">Restaurantes</option>
                  <option value="Estilista">Estilista</option>
                  <option value="Contratista">Contratista</option>
                  <option value="Bienes Raíces">Bienes Raíces</option>
                  <option value="Artista">Artista</option>
                  <option value="Entretenimiento">Entretenimiento</option>
                  <option value="Abogado">Abogado</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Otros">Otros</option>
                </select>
              </div>

              <textarea
                name="needs"
                placeholder="¿Qué necesitas resolver en tu negocio? (opcional, pero ayuda mucho a quien te llame)"
                value={formData.needs}
                onChange={handleChange}
                rows={2}
                className="w-full p-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />

              <button
                type="submit"
                disabled={submitting}
                className="mt-4 w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition shadow-lg disabled:opacity-60"
              >
                {submitting ? 'Guardando...' : 'Guardar Cita'}
              </button>
            </form>
          )}
        </div>
      </Modal>
    </div>
  );
  
};

export default CalendarComponent;
